import { useEffect, useRef, useState } from 'react';
import { useInspectionDetailStore } from '@stores';
import {
  DocumentFormEnum,
  DocumentFormType,
  InspectionSecurityStatusEnum,
  LenderDocumentTypeEnum
} from '@types';
import {
  INSPECTION_SECURITY_CHECK_LABEL_STATUS,
  INSPECTION_SECURITY_CHECK_VARIANT,
  MAX_INSPECTION_COMMENT_LENGTH
} from '@constants';
import _get from 'lodash/get';
import _map from 'lodash/map';
import { format } from 'date-fns';
import StatusChip from '../../../../components/widgets/StatusChip';
import clsx from 'clsx';
import {
  ADD_INSPECTION_COMMENT,
  GET_INSPECTION_COMMENTS,
  GET_INSPECTION_FILE,
  post
} from '@services';
import { useParams } from 'react-router-dom';
import Loader from '../../../../components/widgets/Loader';
import { Button } from '@material-tailwind/react';
import { formatName } from '@utils/formatters';
import EnlargeableImage from '@components/widgets/EnlargeableImage';
interface IProps {
  type: DocumentFormType;
  status: InspectionSecurityStatusEnum;
}

const SecurityCheckCard = ({ type, status }: IProps) => {
  const { activePanel, inspectionDetailData, isCollapsed } =
    useInspectionDetailStore();
  const formattedDate = (date: Date) => format(date, 'dd/MM/yyyy h:mma');
  const [verifyStatus, setVerifyStatus] = useState<string>();
  const [comments, setComments] = useState<any>([]);
  const [subChecks, setSubChecks] = useState<any>([]);
  const [showSuccessMsg, setSuccessMsg] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [comment, setComment] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const isSavingCommentRef = useRef(false);
  const [isLoading, setLoading] = useState(true);
  const [fileId, setFileId] = useState('');
  const [licenseImgsrc, setLicenseImgSrc] = useState<any>();
  const [faceImgsrc, setFaceImgSrc] = useState<any>();
  const isDemo = location.pathname.includes('/demo/inspection');
  let fileTypes: {
    documentType: string;
    fileId: string;
  }[] = [];
  const param = useParams();
  const saveComment = () => {
    if (isSavingCommentRef.current) {
      return;
    }

    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      setCommentError('Please enter a comment before saving.');
      return;
    }

    if (trimmedComment.length > MAX_INSPECTION_COMMENT_LENGTH) {
      setCommentError(
        `Comment must be ${MAX_INSPECTION_COMMENT_LENGTH} characters or less.`
      );
      return;
    }

    setCommentError('');
    isSavingCommentRef.current = true;
    setIsSavingComment(true);

    post(
      ADD_INSPECTION_COMMENT,
      {
        checkType: type,
        documentType: LenderDocumentTypeEnum.SELLER_ID,
        fileId: fileId,
        inspectionId: param.id,
        text: comment
      },
      false
    )
      .then((data) => {
        if (data.commentId) {
          setSuccessMsg(true);
        }
      })
      .catch((e: any) => {
        setCommentError('Failed to save comment. Please try again.');
      })
      .finally(() => {
        isSavingCommentRef.current = false;
        setIsSavingComment(false);
      });
  };
  const toPercentage = (decimal: number): string => {
    return `${Math.round(decimal * 100)}%`;
  };
  useEffect(() => {
    if (
      type === DocumentFormEnum.DETAILS_DRIVER_LICENSE_AUTHENTICITY &&
      inspectionDetailData.inspectionChecks &&
      inspectionDetailData.inspectionChecks.licenseAuthenticity.subChecks
    ) {
      setSubChecks(
        inspectionDetailData.inspectionChecks.licenseAuthenticity.subChecks
      );
    }
    if (
      status &&
      (status === InspectionSecurityStatusEnum.warning ||
        status === InspectionSecurityStatusEnum.failed)
    ) {
      const fileIds = inspectionDetailData.files.filter((file: any) =>
        [
          LenderDocumentTypeEnum.SELLER_ID,
          LenderDocumentTypeEnum.DRIVER_LICENCE_FRONT,
          LenderDocumentTypeEnum.DRIVER_LICENCE_BACK,
          LenderDocumentTypeEnum.FACE
        ].includes(file.documentType)
      );
      const fileId = fileIds.length > 0 && fileIds[0].id;
      if (fileId) {
        setFileId(fileId);
      }
      post(GET_INSPECTION_COMMENTS, { inspectionId: param.id }, false)
        .then((data) => {
          if (data.inspectionComments) {
            setComments(data.inspectionComments);
          }
        })
        .catch((e: any) => {
          console.log(e);
        });
    }
    if (showSuccessMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMsg, type, isLoading]);

  switch (type) {
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_AUTHENTICITY: {
      const licenseAuthenticity =
        inspectionDetailData?.inspectionChecks?.licenseAuthenticity;
      const documentTypeName =
        licenseAuthenticity?.documentType === 'PASSPORT'
          ? 'passport'
          : "driver's licence";

      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            {status && status === InspectionSecurityStatusEnum.warning && (
              <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                Unable to verify with high confidence that the{' '}
                {documentTypeName} provided is authentic.{' '}
              </p>
            )}
            {status && status === InspectionSecurityStatusEnum.failed && (
              <p className="font-medium text-grey-700 text-base bg-error-50 p-4 rounded-md">
                Low confidence in the authenticity of the provided{' '}
                {documentTypeName}.
              </p>
            )}
            <div className="flex flex-col gap-y-6 h-full justify-start items-start w-full">
              {_map(subChecks, (row: any, index: number) => (
                <div key={index} className="flex justify-start gap-x-2">
                  <div key={index}>
                    <p className="text-base text-grey-700">
                      {toPercentage(row.score)} confidence
                    </p>
                    <p className="text-grey-500 text-xs leading-5">
                      {row.name}
                    </p>
                  </div>
                  <div className="justify-self-end">
                    <StatusChip
                      variant={
                        INSPECTION_SECURITY_CHECK_VARIANT[
                          row.status as InspectionSecurityStatusEnum
                        ]
                      }
                      clsName={clsx('border text-xs font-normal leading-5')}
                      label={
                        row.type === 'LICENSE_EXPIRY' &&
                        status === InspectionSecurityStatusEnum.pass
                          ? 'Valid'
                          : row.type === 'LICENSE_EXPIRY' &&
                              status === InspectionSecurityStatusEnum.failed
                            ? 'Expired'
                            : INSPECTION_SECURITY_CHECK_LABEL_STATUS[
                                row.status as keyof typeof INSPECTION_SECURITY_CHECK_LABEL_STATUS
                              ]
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            {!isCollapsed && (
              <div className="w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]"></div>
            )}
          </div>
        </div>
      );
    }
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_EXPIRY:
      fileTypes = inspectionDetailData.files.filter((e: any) =>
        ['DRIVER_LICENCE_FRONT'].includes(e.documentType)
      );
      useEffect(() => {
        if (!isDemo && fileTypes.length > 0) {
          post(
            GET_INSPECTION_FILE,
            { inspectionId: param.id, fileId: fileTypes[0].fileId },
            false
          )
            .then((data) => {
              setLoading(false);
              if (data && data.dataBase64) {
                setLicenseImgSrc(data.dataBase64);
              }
            })
            .catch((e) => {
              setLoading(false);
              console.error(e);
            });
        }
      }, []);
      if (isLoading) return <Loader />;

      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-6">
              <div className="flex flex-col w-full gap-y-6">
                <p className="font-medium text-grey-700 text-base bg-error-50 p-4 rounded-md">
                  The Seller’s driver&apos;s licence had expired at the time of
                  submission.
                </p>
                <div className="flex flex-col gap-8">
                  {licenseImgsrc && !isDemo && (
                    <EnlargeableImage
                      src={`data:image/jpeg;base64,${licenseImgsrc}`}
                      alt="Driver licence"
                    />
                  )}
                  {isDemo && (
                    <EnlargeableImage
                      src="/images/card_front.png"
                      alt="Driver licence"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_DATA_EDIT:
      fileTypes = inspectionDetailData.files.filter(
        (e: any) => e.documentType === 'DRIVER_LICENCE_FRONT'
      );
      useEffect(() => {
        if (!isDemo && fileTypes.length > 0 && !isCollapsed) {
          post(
            GET_INSPECTION_FILE,
            { inspectionId: param.id, fileId: fileTypes[0].fileId },
            false
          )
            .then((data) => {
              setLoading(false);
              // TODO: For MVP hide the add comments function AUT-218
              // setFileId(fileTypes[0].fileId);
              if (data && data.dataBase64) {
                setLicenseImgSrc(data.dataBase64);
              }
            })
            .catch((e) => {
              setLoading(false);
              console.error(e);
            });
        }
      }, []); // Add dependencies

      // Handle loading state in the return part of the component
      if (isLoading) {
        return <Loader />;
      }
      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-6">
              <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                Seller-submitted data differs from OCR-extracted data. This may
                be due to an OCR misread or manual input where OCR retrieval was
                incomplete. Compare the extracted data with the seller’s entry
                and the document image to verify accuracy.
              </p>
              <div className="flex flex-col gap-8">
                {licenseImgsrc && !isDemo && (
                  <EnlargeableImage
                    src={`data:image/jpeg;base64,${licenseImgsrc}`}
                    alt="Driver licence"
                  />
                )}
                {isDemo && (
                  <EnlargeableImage
                    src="/images/card_front.png"
                    alt="Driver licence"
                  />
                )}
              </div>
              <div className="flex flex-col gap-4">
                <h2 className="font-semibold text-[16px]">
                  Seller submitted details
                </h2>
                <div className="flex flex-col border-y border-grey-200 w-full">
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Given Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.firstName?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Middle Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.middleName?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Family Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.lastName?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Date Of Birth
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.dateOfBirth?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Licence Number
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.licenceNumber?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Licence Class
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.licenceType?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Date Of Expiry
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.dateOfExpiry?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Card Number
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.cardNumber?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Address Line 1
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.addressLine1?.userEditValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Address Line 2
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.addressLine2?.userEditValue
                      }
                    </p>
                  </div>

                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      State
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.state?.userEditValue
                      }
                    </p>
                  </div>
                </div>
                <h2 className="font-semibold text-[16px]">
                  OCR-extracted data
                </h2>
                <div className="flex flex-col border-y border-grey-200 w-full">
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Given Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.firstName?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Middle Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.middleName?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Family Name
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.lastName?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Date Of Birth
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.dateOfBirth?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Licence Number
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.licenceNumber?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Licence Class
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.licenceType?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Date Of Expiry
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.dateOfExpiry?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Card Number
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.cardNumber?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Address Line 1
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.addressLine1?.ocrValue
                      }
                    </p>
                  </div>
                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      Address Line 2
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.addressLine2?.ocrValue
                      }
                    </p>
                  </div>

                  <div className="grid grid-cols-2 py-3.5 border-b border-grey-200 last:border-none">
                    <p className="text-grey-500 text-sm font-medium leading-5">
                      State
                    </p>
                    <p className="text-sm font-medium text-grey-950 text-right">
                      {
                        inspectionDetailData.inspectionChecks.licenseEdits
                          ?.state?.ocrValue
                      }
                    </p>
                  </div>
                </div>
              </div>
              {fileId && fileId !== '' && (
                <div className="flex flex-col bg-grey-50 p-4 gap-6 border border-grey-200 rounded-lg">
                  <p className="font-medium text-base text-grey-700">
                    Add a comment to the verification report that provides
                    context to potential differences.
                  </p>
                  <ul>
                    {_map(
                      comments,
                      (comment: any, index: number) =>
                        _get(comment, 'text') && (
                          <li key={index} className="text-sm text-grey-700">
                            <span className="font-semibold">
                              {' '}
                              {formatName(
                                comment.user.firstName,
                                comment.user.lastName
                              )}
                              {comment?.userRole &&
                                ' (' +
                                  comment?.userRole.charAt(0).toUpperCase() +
                                  comment?.userRole.slice(1).toLowerCase() +
                                  ')'}{' '}
                            </span>
                            <span>
                              {' '}
                              {formattedDate(new Date(comment.createTime))}{' '}
                            </span>
                            <p> {comment.text} </p>
                          </li>
                        )
                    )}
                  </ul>
                  <div className="flex flex-col gap-y-1.5">
                    <span className="font-medium text-sm text-grey-700">
                      Add comment to verification report (optional)
                    </span>
                    <textarea
                      className="w-full h-32 border rounded-lg py-3 px-3.5 gap-2 border-grey-300"
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                        setCommentError('');
                      }}
                      placeholder="Add a comment"
                      maxLength={MAX_INSPECTION_COMMENT_LENGTH}
                      disabled={isSavingComment}
                    />
                    <span className="self-end text-xs text-grey-500">
                      {comment.length}/{MAX_INSPECTION_COMMENT_LENGTH}
                    </span>
                    {showSuccessMsg && (
                      <span className="self-end text-sm text-grey-600">
                        Saved!
                      </span>
                    )}
                    {commentError && (
                      <span className="self-end text-sm text-error-500">
                        {commentError}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {!isCollapsed && (
                <div className="w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]">
                  <Button
                    className={clsx(
                      'font-semibold bg-primary capitalize w-full md:w-1/2 text-base whitespace-nowrap',
                      (comment === '' || fileId === '') && 'hidden'
                    )}
                    onClick={saveComment}
                    disabled={isSavingComment}>
                    {isSavingComment ? 'Saving...' : 'Save comment'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_FACE_MATCH:
      fileTypes = inspectionDetailData.files.filter((e: any) =>
        ['DRIVER_LICENCE_FRONT', 'FACE'].includes(e.documentType)
      );
      useEffect(() => {
        if (!isDemo && fileTypes.length > 0) {
          post(
            GET_INSPECTION_FILE,
            { inspectionId: param.id, fileId: fileTypes[0].fileId },
            false
          )
            .then((data) => {
              setLoading(false);
              if (data && data.dataBase64) {
                if (fileTypes[0].documentType === 'DRIVER_LICENCE_FRONT') {
                  setLicenseImgSrc(data.dataBase64);
                } else {
                  setFaceImgSrc(data.dataBase64);
                }
                post(
                  GET_INSPECTION_FILE,
                  { inspectionId: param.id, fileId: fileTypes[1].fileId },
                  false
                )
                  .then((data) => {
                    setLoading(false);
                    if (data && data.dataBase64) {
                      if (
                        fileTypes[1]?.documentType === 'DRIVER_LICENCE_FRONT'
                      ) {
                        setLicenseImgSrc(data.dataBase64);
                      } else {
                        setFaceImgSrc(data.dataBase64);
                      }
                    }
                  })
                  .catch((e) => {
                    setLoading(false);
                    console.error(e);
                  });
              }
            })
            .catch((e) => {
              setLoading(false);
              console.error(e);
            });
        }
      }, []);
      if (isLoading) return <Loader />;

      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-6">
              {status && status === InspectionSecurityStatusEnum.warning && (
                <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                  Unable to verify with high confidence that the picture on the
                  driver&apos;s licence matches the selfie provided.{' '}
                </p>
              )}
              {status && status === InspectionSecurityStatusEnum.failed && (
                <p className="font-medium text-grey-700 text-base bg-error-50 p-4 rounded-md">
                  Low confidence that the picture on the driver&apos;s licence
                  matches the selfie provided.
                </p>
              )}
              <div className="flex flex-col gap-2">
                {licenseImgsrc && !isDemo && (
                  <EnlargeableImage
                    src={`data:image/jpeg;base64,${licenseImgsrc}`}
                    alt="Driver licence"
                  />
                )}
                {isDemo && (
                  <EnlargeableImage
                    src="/images/card_front.png"
                    alt="Driver licence"
                  />
                )}
                {faceImgsrc && !isDemo && (
                  <EnlargeableImage
                    src={`data:image/jpeg;base64,${faceImgsrc}`}
                    alt="Selfie"
                  />
                )}
                {isDemo && (
                  <EnlargeableImage
                    src="/images/demo_face_match.png"
                    alt="Selfie"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_LIVENESS:
      fileTypes = inspectionDetailData.files.filter((e: any) =>
        ['FACE'].includes(e.documentType)
      );

      useEffect(() => {
        if (!isDemo && fileTypes.length > 0) {
          post(
            GET_INSPECTION_FILE,
            { inspectionId: param.id, fileId: fileTypes[0].fileId },
            false
          )
            .then((data) => {
              setLoading(false);
              if (data && data.dataBase64) {
                setFaceImgSrc(data.dataBase64);
              }
            })
            .catch((e) => {
              setLoading(false);
              console.error(e);
            });
        }
      }, []);

      if (isLoading) return <Loader />;

      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 min-h-0">
            <div className="flex flex-col w-full gap-y-6">
              {status && status === InspectionSecurityStatusEnum.warning && (
                <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                  Unable to verify with high confidence that selfie was
                  completed by an actual person.
                </p>
              )}
              {status && status === InspectionSecurityStatusEnum.failed && (
                <p className="font-medium text-grey-700 text-base bg-error-50 p-4 rounded-md">
                  Low confidence that selfie process was completed by an actual
                  person.
                </p>
              )}
              <div className="flex flex-col gap-8 justify-center">
                {faceImgsrc && !isDemo && (
                  <EnlargeableImage
                    src={`data:image/jpeg;base64,${faceImgsrc}`}
                    alt="Selfie"
                  />
                )}
                {isDemo && (
                  <EnlargeableImage
                    src="/images/demo_face_match.png"
                    alt="Selfie"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    case DocumentFormEnum.DETAILS_DRIVER_LICENSE_OFFICIAL_SOURCE:
      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-6">
              <p className="font-medium text-grey-700 text-base bg-error-50 p-4 rounded-md">
                Checking against government databases, the identification has
                failed the official source check.
              </p>
            </div>
          </div>
        </div>
      );
    default:
      return <> </>;
  }
};
export default SecurityCheckCard;
